import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users2, Sparkles, Loader2, Check, Palette, Search, Plus, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const TABS = [
  { key: "library", label: "Library", icon: Sparkles },
  { key: "build",   label: "Build custom", icon: Palette },
  { key: "roster",  label: "My roster", icon: Users2 },
];

export default function Influencers() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("library");

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-slate-200 px-8 py-5 flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Influencers</div>
          <h1 className="font-display font-bold text-2xl mt-1">Your cast of AI-native creators.</h1>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`inf-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-3.5 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  tab === t.key ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-8">
        {tab === "library" && <LibraryTab qc={qc} switchToRoster={() => setTab("roster")} />}
        {tab === "build"   && <BuilderTab qc={qc} switchToRoster={() => setTab("roster")} />}
        {tab === "roster"  && <RosterTab />}
      </div>
    </div>
  );
}

/* ============= LIBRARY TAB ============= */
function LibraryTab({ qc, switchToRoster }) {
  const [filters, setFilters] = useState({ ethnicity: "", gender: "", age_group: "" });
  const [adopting, setAdopting] = useState(null);

  const { data: tax } = useQuery({
    queryKey: ["taxonomies"],
    queryFn: () => api.get("/avatars/taxonomies").then(r => r.data),
  });
  const { data } = useQuery({
    queryKey: ["presets", filters],
    queryFn: () => {
      const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => !!v));
      return api.get(`/avatars/presets?${p.toString()}`).then(r => r.data);
    },
  });

  const adopt = async (preset) => {
    setAdopting(preset.id);
    try {
      const { data } = await api.post("/avatars/adopt-preset", { preset_id: preset.id });
      toast.success(`Added ${data.influencer.name} to your roster — fresh Nano Banana portrait rendered`);
      qc.invalidateQueries({ queryKey: ["influencers"] });
      switchToRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adopt preset");
    } finally {
      setAdopting(null);
    }
  };

  const presets = data?.presets || [];

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Search className="w-4 h-4" /> Filters:
        </div>
        <FilterSelect
          value={filters.ethnicity}
          onChange={(v) => setFilters({ ...filters, ethnicity: v })}
          placeholder="Any ethnicity"
          options={(tax?.ethnicities || []).map((e) => ({ value: e.key, label: e.label }))}
          testid="filter-ethnicity"
        />
        <FilterSelect
          value={filters.gender}
          onChange={(v) => setFilters({ ...filters, gender: v })}
          placeholder="Any gender"
          options={(tax?.genders || []).map((g) => ({ value: g, label: g.replace("_", "-") }))}
          testid="filter-gender"
        />
        <FilterSelect
          value={filters.age_group}
          onChange={(v) => setFilters({ ...filters, age_group: v })}
          placeholder="Any age"
          options={(tax?.age_groups || []).map((a) => ({ value: a, label: a }))}
          testid="filter-age"
        />
        {(filters.ethnicity || filters.gender || filters.age_group) && (
          <button
            data-testid="filter-clear"
            onClick={() => setFilters({ ethnicity: "", gender: "", age_group: "" })}
            className="text-xs text-blue-600 hover:underline"
          >
            Clear
          </button>
        )}
        <div className="ml-auto text-xs text-slate-400 font-mono uppercase tracking-widest">{presets.length} presets</div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {presets.map((p) => (
          <div
            key={p.id}
            data-testid={`preset-${p.id}`}
            className="group rounded-xl border border-slate-200 overflow-hidden hover:border-blue-300 hover:shadow-md transition-all bg-white"
          >
            <div className="aspect-[4/5] bg-slate-100 overflow-hidden relative">
              <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm rounded-md px-2 py-0.5 text-[9px] uppercase font-mono tracking-widest text-slate-700 font-medium">
                {p.age} · {p.age_group}
              </div>
            </div>
            <div className="p-3.5">
              <div className="font-display font-bold text-[15px] text-slate-900">{p.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{p.vibe}</div>
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                <span className="font-mono text-[9px] uppercase tracking-wide text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{p.ethnicity.replace("_", " ")}</span>
                <span className="font-mono text-[9px] uppercase tracking-wide text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{p.gender}</span>
              </div>
              <Button
                data-testid={`adopt-${p.id}`}
                onClick={() => adopt(p)}
                disabled={adopting === p.id}
                size="sm"
                className="w-full mt-3 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                {adopting === p.id ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Rendering portrait…</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />Add to roster</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-[170px] bg-white border-slate-300 rounded-md">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-white border-slate-200 rounded-md">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ============= BUILDER TAB ============= */
function BuilderTab({ qc, switchToRoster }) {
  const [form, setForm] = useState({
    name: "",
    gender: "female",
    ethnicity: "south_asian",
    age: 28,
    wardrobe: "",
    personality: "",
    vibe: "",
    look_details: "",
    variants: 4,
  });
  const [result, setResult] = useState(null); // { variants: [...] }
  const [picks, setPicks] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: tax } = useQuery({
    queryKey: ["taxonomies"],
    queryFn: () => api.get("/avatars/taxonomies").then(r => r.data),
  });

  const generate = async () => {
    if (!form.name.trim()) { toast.error("Give your influencer a name"); return; }
    setGenerating(true);
    setResult(null);
    setPicks(new Set());
    try {
      const { data } = await api.post("/avatars/build", form);
      setResult(data);
      const okCount = (data.variants || []).filter(v => v.image_url).length;
      if (okCount === 0) toast.error("All variants failed — try again or refine the brief");
      else toast.success(`${okCount} variants rendered — pick the ones you like`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const toggle = (variantIdx) => {
    const s = new Set(picks);
    s.has(variantIdx) ? s.delete(variantIdx) : s.add(variantIdx);
    setPicks(s);
  };

  const save = async () => {
    if (picks.size === 0) { toast.error("Pick at least one variant"); return; }
    setSaving(true);
    try {
      const selectedVariants = result.variants.filter((v) => picks.has(v.variant) && v.image_url);
      const { data } = await api.post("/avatars/save", {
        ...form,
        picks: selectedVariants,
      });
      toast.success(`Saved ${data.count} influencer${data.count > 1 ? "s" : ""} to your roster`);
      qc.invalidateQueries({ queryKey: ["influencers"] });
      switchToRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Brief form */}
      <div className="lg:col-span-5 space-y-4">
        <div className="rounded-xl border border-slate-200 p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">// Define your influencer</div>

          <FormField label="Name">
            <Input
              data-testid="build-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Aisha Verma"
              className="h-10 border-slate-300 rounded-md"
            />
          </FormField>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <FormField label="Gender">
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="build-gender" className="h-10 border-slate-300 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white rounded-md">
                  {(tax?.genders || []).map((g) => <SelectItem key={g} value={g}>{g.replace("_", "-")}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Ethnicity">
              <Select value={form.ethnicity} onValueChange={(v) => setForm({ ...form, ethnicity: v })}>
                <SelectTrigger data-testid="build-ethnicity" className="h-10 border-slate-300 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white rounded-md">
                  {(tax?.ethnicities || []).map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Age">
              <Input
                data-testid="build-age"
                type="number"
                value={form.age}
                onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 25 })}
                min={13} max={80}
                className="h-10 border-slate-300 rounded-md"
              />
            </FormField>
          </div>

          <FormField label="Wardrobe / aesthetic" className="mt-3">
            <Input
              data-testid="build-wardrobe"
              value={form.wardrobe}
              onChange={(e) => setForm({ ...form, wardrobe: e.target.value })}
              placeholder="linen kurta, gold hoops, minimalist"
              className="h-10 border-slate-300 rounded-md"
            />
          </FormField>

          <FormField label="Vibe / archetype (one line)" className="mt-3">
            <Input
              data-testid="build-vibe"
              value={form.vibe}
              onChange={(e) => setForm({ ...form, vibe: e.target.value })}
              placeholder="quiet-confident fashion student"
              className="h-10 border-slate-300 rounded-md"
            />
          </FormField>

          <FormField label="Personality" className="mt-3">
            <Input
              data-testid="build-personality"
              value={form.personality}
              onChange={(e) => setForm({ ...form, personality: e.target.value })}
              placeholder="grounded, warm, subtle humor"
              className="h-10 border-slate-300 rounded-md"
            />
          </FormField>

          <FormField label="Additional look details (optional)" className="mt-3">
            <Textarea
              data-testid="build-look"
              value={form.look_details}
              onChange={(e) => setForm({ ...form, look_details: e.target.value })}
              placeholder="soft freckles, wavy shoulder-length hair, warm smile, Delhi apartment backdrop"
              rows={3}
              className="border-slate-300 rounded-md text-sm"
            />
          </FormField>

          <div className="grid grid-cols-3 gap-3 items-end mt-4">
            <FormField label="Variants">
              <Input
                data-testid="build-variants"
                type="number"
                min={1} max={6}
                value={form.variants}
                onChange={(e) => setForm({ ...form, variants: Math.max(1, Math.min(6, parseInt(e.target.value) || 4)) })}
                className="h-10 border-slate-300 rounded-md"
              />
            </FormField>
            <div className="col-span-2">
              <Button
                data-testid="build-generate"
                onClick={generate}
                disabled={generating}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rendering {form.variants}…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate {form.variants} variants</>}
              </Button>
            </div>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-2">
            Free · uses Nano Banana. Only tokens are spent when you render videos, not portraits.
          </div>
        </div>
      </div>

      {/* Variant results */}
      <div className="lg:col-span-7">
        {!result && !generating && (
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-12 text-center text-sm text-slate-400 h-full flex flex-col items-center justify-center">
            <Palette className="w-8 h-8 mb-3 text-slate-300" />
            Fill the brief and hit generate. Nano Banana will render {form.variants} variations — pick the ones you love (or all of them).
          </div>
        )}
        {generating && (
          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-12 text-center h-full flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 mb-3 text-blue-600 animate-spin" />
            <div className="text-sm text-blue-800 font-medium">Rendering {form.variants} portraits…</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">10-25 seconds</div>
          </div>
        )}
        {result && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                // {result.variants.length} variants · {picks.size} selected
              </div>
              <Button
                data-testid="build-save"
                onClick={save}
                disabled={saving || picks.size === 0}
                className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:bg-slate-200"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Save {picks.size} to roster</>}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {result.variants.map((v) => {
                const picked = picks.has(v.variant);
                const failed = !v.image_url;
                return (
                  <button
                    key={v.variant}
                    data-testid={`variant-${v.variant}`}
                    onClick={() => !failed && toggle(v.variant)}
                    disabled={failed}
                    className={`relative rounded-lg overflow-hidden border-2 aspect-[4/5] transition-all ${
                      failed
                        ? "border-red-200 bg-red-50 cursor-not-allowed"
                        : picked
                        ? "border-blue-600 ring-2 ring-blue-200"
                        : "border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {failed ? (
                      <div className="w-full h-full flex flex-col items-center justify-center p-3 text-xs text-red-600 text-center">
                        Variant failed — retry
                      </div>
                    ) : (
                      <>
                        <img src={v.image_url} alt={`variant ${v.variant}`} className="w-full h-full object-cover" />
                        {picked && (
                          <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                              <Check className="w-5 h-5 text-white" strokeWidth={3} />
                            </div>
                          </div>
                        )}
                        <div className="absolute top-1.5 left-1.5 bg-white/95 backdrop-blur rounded px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-slate-700 font-bold">
                          V{v.variant}
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* ============= ROSTER TAB ============= */
function RosterTab() {
  const { data } = useQuery({
    queryKey: ["influencers"],
    queryFn: () => api.get("/influencers").then(r => r.data),
  });
  const list = data || [];
  if (list.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-12 text-center text-sm text-slate-400">
        <Users2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
        No influencers yet. Adopt one from the Library or Build a custom character.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
      {list.map((inf) => (
        <div key={inf.id} data-testid={`roster-${inf.id}`} className="rounded-xl border border-slate-200 overflow-hidden bg-white hover:shadow-md transition-all">
          <div className="aspect-[4/5] bg-slate-100 overflow-hidden">
            {inf.avatar_url
              ? <img src={inf.avatar_url} alt={inf.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-slate-300"><Users2 className="w-8 h-8" /></div>}
          </div>
          <div className="p-3.5">
            <div className="font-display font-bold text-[15px] text-slate-900 truncate">{inf.name}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{inf.archetype}</div>
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-wide text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{inf.niche}</span>
              <span className="font-mono text-[9px] uppercase tracking-wide text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{inf.voice}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
